import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { volumePages } from '../data/volumePages';
import { PageLightbox } from './PageLightbox';
const DESKTOP_QUERY = '(min-width: 1024px)';
const DESKTOP_VISIBLE = 3;
const arrowClass = 'flex h-10 w-10 flex-none items-center justify-center rounded-full border border-sand-dark bg-white text-sage shadow-card transition-colors duration-150 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-4 focus-visible:ring-ochre/60';
export function VolumeCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [active, setActive] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const total = volumePages.length;
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  /** Posición de scroll en la que la slide i queda "encajada". */
  const snapLeft = useCallback((i: number) => {
    const track = trackRef.current;
    const slide = track?.children[i] as HTMLElement | undefined;
    if (!track || !slide) return 0;
    if (isDesktop) return slide.offsetLeft;
    return slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2;
  }, [isDesktop]);
  const handleScroll = () => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const track = trackRef.current;
      if (!track) return;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < total; i++) {
        const dist = Math.abs(snapLeft(i) - track.scrollLeft);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      const maxStart = isDesktop ? total - DESKTOP_VISIBLE : total - 1;
      setActive(Math.min(best, maxStart));
    });
  };
  const goTo = (i: number) => {
    const track = trackRef.current;
    if (!track) return;
    const maxStart = isDesktop ? total - DESKTOP_VISIBLE : total - 1;
    const target = Math.max(0, Math.min(maxStart, i));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: snapLeft(target),
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const maxStart = isDesktop ? total - DESKTOP_VISIBLE : total - 1;
  const isDotActive = (i: number) => isDesktop ? i >= active && i < active + DESKTOP_VISIBLE : i === active;
  return <section aria-labelledby="volume-pages-heading" className="w-full bg-sand" data-id="element-387" data-mp="19bdszhhob4g">
      <div className="mx-auto max-w-6xl px-5 pt-16 sm:pt-20" data-id="element-388" data-mp="177d0qevzs8e">
        <div className="text-center" data-id="element-389" data-mp="3sge6jvcz6pgs">
          <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-terra-deep" data-id="element-390" data-mp="3tznqvikhu64">
            A look inside · Volume 5
          </p>
          <h2 id="volume-pages-heading" className="chalk-underline mt-3 font-display text-[28px] text-sage sm:text-5xl" data-id="element-391" data-mp="8s16ffc2zrh2">
            Real pages, ready to use in session
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-392" data-mp="iicmu1cybt4u">
            Every experiment fits on one page: offer line, steps, grades and
            safety flag.
          </p>
        </div>
      </div>

      <div className="relative mx-auto mt-10 max-w-6xl lg:px-16" role="region" aria-roledescription="carousel" aria-label="Volume 5 sample pages" data-id="element-393" data-mp="3lzuqdwloydzg">
        <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous pages" className={`${arrowClass} absolute left-2 top-1/2 hidden -translate-y-1/2 lg:flex`} data-id="element-394" data-mp="3vzdaqe5ik1s1">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-395" data-mp="18sd1ol2bqy30" />
        </button>

        <div ref={trackRef} onScroll={handleScroll} className="relative flex snap-x snap-mandatory gap-4 overflow-x-auto px-[10%] pb-4 pt-1 lg:gap-6 lg:px-0 [&::-webkit-scrollbar]:hidden" style={{
        scrollbarWidth: 'none'
      }} data-id="element-396" data-mp="14rkq3l5zch3k">
          {volumePages.map((page, i) => <div key={page.src} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${total}`} className="w-[80%] flex-none snap-center lg:w-[calc((100%-3rem)/3)] lg:snap-start" data-id="element-397" data-mp="1rqz3y39kuv8i" data-mpi={i}>
              <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Open full screen: ${page.alt}`} className="block w-full rounded-[12px] bg-cream p-2 shadow-card focus:outline-none focus-visible:ring-4 focus-visible:ring-ochre/60" data-id="element-398" data-mp="1wiqvoh4gz708">
                <img src={page.src} alt={page.alt} width={1125} height={2000} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" className="aspect-[9/16] w-full rounded-[8px] bg-white object-contain" data-id="element-399" data-mp="2q3hyqt5r65nv" />
              </button>
            </div>)}
        </div>

        <button type="button" onClick={() => goTo(active + 1)} disabled={active >= maxStart} aria-label="Next pages" className={`${arrowClass} absolute right-2 top-1/2 hidden -translate-y-1/2 lg:flex`} data-id="element-400" data-mp="26do323zx36ed">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-401" data-mp="22cck2hbjtjf5" />
        </button>
      </div>

      <div className="mx-auto flex max-w-6xl items-center justify-center gap-5 px-5 pb-16 pt-4 sm:pb-20" data-id="element-402" data-mp="1nfv6djfvzspf">
        <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={`${arrowClass} lg:hidden`} data-id="element-403" data-mp="1sf7ue7ihv5tl">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-404" data-mp="14ibl951x1522" />
        </button>

        <div className="flex items-center gap-1" data-id="element-405" data-mp="zjqkn4ir4jd3">
          {volumePages.map((page, i) => <button key={page.src} type="button" onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === active ? 'true' : undefined} className="flex h-6 w-6 items-center justify-center rounded-full focus:outline-none focus-visible:ring-4 focus-visible:ring-ochre/60" data-id="element-406" data-mp="ulp0shdi4g0k" data-mpi={i}>
              <span className={`block h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${isDotActive(i) ? 'w-6 bg-terra-deep' : 'w-2.5 bg-sage/30'}`} data-id="element-407" data-mp="1dbwyul2cgg9w" />
            </button>)}
        </div>

        <button type="button" onClick={() => goTo(active + 1)} disabled={active >= maxStart} aria-label="Next page" className={`${arrowClass} lg:hidden`} data-id="element-408" data-mp="2djv49yx1tu3u">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-409" data-mp="2nhiy8mmtyxky" />
        </button>
      </div>

      <AnimatePresence data-id="element-410" data-mp="2s99ch3ow25hn">
        {lightboxIndex !== null && <PageLightbox pages={volumePages} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} data-id="element-411" data-mp="2sau8kxqupwhm" />}
      </AnimatePresence>
    </section>;
}